// src/components/ui/dashboard/recommended-career-paths.tsx
"use client"

import { useState, useRef, useEffect, useCallback, useMemo } from "react"
import {
  ChefHat, Stethoscope, Atom, Palette, Code, Building2,
  ChevronDown, ChevronUp, ArrowRight, Sparkles, Briefcase,
  Scale, GraduationCap, TrendingUp, Leaf, Cpu, Music, Globe,
} from "lucide-react"
import { useNavigate } from "react-router"
import { StudentStore } from "@/hooks/stores/StudentStore"
import { getAllNodes } from "@/data/career-tree"

// Static full Tailwind classes — Tailwind JIT requires complete strings at build time
const PALETTES = [
  { strip: "bg-violet-500", iconWrap: "bg-violet-400/30", iconColor: "text-violet-100", btn: "bg-violet-600 hover:bg-violet-700", scoreBg: "bg-violet-50", scoreText: "text-violet-700", border: "border-violet-100" },
  { strip: "bg-rose-500",   iconWrap: "bg-rose-400/30",   iconColor: "text-rose-100",   btn: "bg-rose-500 hover:bg-rose-600",     scoreBg: "bg-rose-50",   scoreText: "text-rose-700",   border: "border-rose-100"   },
  { strip: "bg-teal-500",   iconWrap: "bg-teal-400/30",   iconColor: "text-teal-100",   btn: "bg-teal-600 hover:bg-teal-700",     scoreBg: "bg-teal-50",   scoreText: "text-teal-700",   border: "border-teal-100"   },
  { strip: "bg-amber-500",  iconWrap: "bg-amber-400/30",  iconColor: "text-amber-100",  btn: "bg-amber-500 hover:bg-amber-600",   scoreBg: "bg-amber-50",  scoreText: "text-amber-700",  border: "border-amber-100"  },
  { strip: "bg-blue-500",   iconWrap: "bg-blue-400/30",   iconColor: "text-blue-100",   btn: "bg-blue-600 hover:bg-blue-700",     scoreBg: "bg-blue-50",   scoreText: "text-blue-700",   border: "border-blue-100"   },
  { strip: "bg-emerald-500",iconWrap: "bg-emerald-400/30",iconColor: "text-emerald-100",btn: "bg-emerald-600 hover:bg-emerald-700",scoreBg: "bg-emerald-50",scoreText: "text-emerald-700",border: "border-emerald-100"},
]

// Map career name keywords → icon + skills
const CAREER_META: { keywords: string[]; icon: React.ElementType; skills: string[]; bio: string }[] = [
  { keywords: ["software", "developer", "engineer", "programmer", "coding", "web"], icon: Code,         skills: ["Programming", "Problem Solving", "Teamwork"],             bio: "Build applications, systems, and tools that power the digital world."         },
  { keywords: ["doctor", "physician", "medicine", "medical"],                       icon: Stethoscope,  skills: ["Empathy", "Analytical Thinking", "Decision Making"],      bio: "Diagnose and treat illness to improve patient health and wellbeing."           },
  { keywords: ["nurse", "nursing", "health"],                                       icon: Stethoscope,  skills: ["Empathy", "Attention to Detail", "Medical Knowledge"],    bio: "Provide frontline patient care and support in healthcare settings."            },
  { keywords: ["chef", "cook", "culinary", "food"],                                 icon: ChefHat,      skills: ["Creativity", "Time Management", "Leadership"],             bio: "Create memorable dishes and lead kitchen operations."                          },
  { keywords: ["design", "graphic", "art", "creative", "visual", "animation"],     icon: Palette,      skills: ["Creativity", "Visual Communication", "Software"],         bio: "Shape how ideas look and feel through art and visual storytelling."            },
  { keywords: ["architect", "architecture", "build", "construction"],               icon: Building2,    skills: ["Design Skills", "Technical Drawing", "Project Mgmt"],     bio: "Design spaces that stand the test of time — from homes to skyscrapers."       },
  { keywords: ["lawyer", "law", "legal", "attorney", "advocate"],                   icon: Scale,        skills: ["Reasoning", "Communication", "Research"],                 bio: "Uphold justice and provide counsel on complex legal matters."                   },
  { keywords: ["teacher", "education", "professor", "lecturer", "tutor"],           icon: GraduationCap,skills: ["Communication", "Patience", "Subject Mastery"],           bio: "Inspire and shape the next generation through knowledge and mentorship."       },
  { keywords: ["business", "entrepreneur", "management", "manager", "executive"],   icon: Briefcase,    skills: ["Leadership", "Strategy", "Communication"],                bio: "Lead organisations and create value through innovative business thinking."     },
  { keywords: ["finance", "accountant", "banker", "economics", "investment"],       icon: TrendingUp,   skills: ["Numerical Analysis", "Attention to Detail", "Strategy"], bio: "Manage money, investments, and financial systems for individuals and firms."   },
  { keywords: ["science", "researcher", "biologist", "chemist", "physicist"],       icon: Atom,         skills: ["Analytical Thinking", "Mathematics", "Research"],         bio: "Explore the fundamental principles that govern the universe."                  },
  { keywords: ["environment", "agriculture", "farming", "ecology", "conservation"], icon: Leaf,         skills: ["Problem Solving", "Fieldwork", "Biology"],                bio: "Protect natural resources and build a sustainable future."                     },
  { keywords: ["technology", "it", "data", "cyber", "ai", "machine learning"],      icon: Cpu,          skills: ["Technical Skills", "Critical Thinking", "Innovation"],    bio: "Drive the future of computing, data, and artificial intelligence."             },
  { keywords: ["music", "musician", "performer", "artist", "entertainer"],          icon: Music,        skills: ["Creativity", "Performance", "Discipline"],                bio: "Create and perform art that moves and inspires audiences worldwide."           },
  { keywords: ["international", "diplomat", "global", "languages", "relations"],    icon: Globe,        skills: ["Communication", "Cultural Awareness", "Languages"],       bio: "Bridge cultures and shape global policy through diplomacy and understanding."  },
]

const DEFAULT_META = { icon: Briefcase, skills: ["Critical Thinking", "Communication", "Adaptability"], bio: "A rewarding career path that offers growth and impact." }

function getCareerMeta(careerName: string) {
  const lower = careerName.toLowerCase()
  return CAREER_META.find(m => m.keywords.some(k => lower.includes(k))) ?? DEFAULT_META
}

interface CareerMatchData {
  career: string
  matchPercentage: number
  confidence?: number
}

interface CareerPath {
  icon: React.ElementType
  title: string
  bio: string
  match: number
  skills: string[]
}

function CareerCard({
  career, palette, compact = false, onExplore,
}: { career: CareerPath; palette: (typeof PALETTES)[0]; compact?: boolean; onExplore: () => void }) {
  const Icon = career.icon
  return (
    <div className={`bg-white rounded-2xl border ${palette.border} shadow-sm flex flex-col overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5 ${compact ? "w-48 shrink-0" : "w-full"}`}>
      <div className={`${palette.strip} px-4 pt-4 pb-5 relative`}>
        <div className={`w-11 h-11 rounded-xl ${palette.iconWrap} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${palette.iconColor}`} />
        </div>
        <div className={`absolute bottom-0 right-3 translate-y-1/2 ${palette.scoreBg} ${palette.scoreText} text-[11px] font-black px-2.5 py-0.5 rounded-full border border-white shadow-sm`}>
          {career.match}% match
        </div>
      </div>
      <div className="px-3 pt-4 pb-3 flex flex-col flex-1">
        <p className="text-[13px] font-black text-slate-900 leading-tight mb-1">{career.title}</p>
        <p className="text-[11px] text-slate-500 leading-snug line-clamp-2 flex-1">{career.bio}</p>
        {!compact && (
          <div className="flex flex-wrap gap-1 mt-2 mb-1">
            {career.skills.map((s) => (
              <span key={s} className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-medium">{s}</span>
            ))}
          </div>
        )}
        <button onClick={onExplore} className={`mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl ${palette.btn} text-white text-xs font-bold transition-all active:scale-95`}>
          Explore <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  )
}

function AutoScrollRow({ children, speed = 0.6 }: { children: React.ReactNode; speed?: number }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const pausedRef = useRef(false)
  const posRef = useRef(0)
  const rafRef = useRef<number>(0)

  const tick = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    if (!pausedRef.current) {
      posRef.current += speed
      const half = track.scrollWidth / 2
      if (posRef.current >= half) posRef.current = 0
      track.style.transform = `translateX(-${posRef.current}px)`
    }
    rafRef.current = requestAnimationFrame(tick)
  }, [speed])

  useEffect(() => {
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [tick])

  return (
    <div className="overflow-hidden" onMouseEnter={() => { pausedRef.current = true }} onMouseLeave={() => { pausedRef.current = false }} onTouchStart={() => { pausedRef.current = true }} onTouchEnd={() => { pausedRef.current = false }}>
      <div ref={trackRef} className="flex gap-3 will-change-transform">
        {children}
        {children}
      </div>
    </div>
  )
}

// Fallback paths shown when student has no career matches yet
const FALLBACK_PATHS: CareerPath[] = [
  { icon: Code,         title: "Software Developer",  bio: "Design, develop, and maintain software applications and systems.",              match: 0, skills: ["Programming", "Problem Solving", "Collaboration"] },
  { icon: Atom,         title: "Physicist",            bio: "Research physical phenomena, develop theories, and apply principles.",           match: 0, skills: ["Analytical Thinking", "Mathematics", "Research"]   },
  { icon: Stethoscope,  title: "Nurse",                bio: "Provide patient care, administer treatments, and support doctors.",              match: 0, skills: ["Empathy", "Attention to Detail", "Medical"]        },
  { icon: ChefHat,      title: "Chef",                 bio: "Create culinary masterpieces and manage kitchen operations.",                    match: 0, skills: ["Creativity", "Time Management", "Leadership"]      },
  { icon: Palette,      title: "Graphic Designer",     bio: "Create visual concepts that communicate ideas and captivate audiences.",         match: 0, skills: ["Creativity", "Software", "Visual Communication"]   },
  { icon: Building2,    title: "Architect",            bio: "Plan and design buildings and oversee construction with structural integrity.",  match: 0, skills: ["Design Skills", "Technical Drawing", "Project Mgmt"]},
]

const FOR_YOU_MAX = 6

function scoreNode(node: { traits: { traitId: string; minimumThreshold: number }[] }, profile: Record<string, number> | null): number | null {
  if (!profile || !node.traits?.length) return null
  let raw = 0, max = 0
  for (const { traitId, minimumThreshold } of node.traits) {
    raw += ((profile[traitId] ?? 0) / 100) * minimumThreshold
    max += minimumThreshold
  }
  return max > 0 ? Math.round((raw / max) * 100) : null
}

interface RecommendedCareerPathsProps {
  careerMatches?: CareerMatchData[]
}

export function RecommendedCareerPaths({ careerMatches }: RecommendedCareerPathsProps) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  const traitProfile = StudentStore((s) => s.student?.traitProfile ?? null)

  // Derive top matches from trait profile using the career tree
  const derivedPaths = useMemo<CareerPath[]>(() => {
    if (!traitProfile) return []
    const allNodes = getAllNodes()
    const fieldById = Object.fromEntries(
      allNodes.filter(n => n.level === "field").map(n => [n.nodeId, n])
    )
    return allNodes
      .filter(n => n.level === "specialization")
      .map(n => {
        const scoringNode = n.traits?.length ? n : (fieldById[n.parentId] ?? n)
        const match = scoreNode(scoringNode, traitProfile as Record<string, number>) ?? 0
        const meta = getCareerMeta(n.name)
        return { icon: meta.icon, title: n.name, bio: n.description || meta.bio, match, skills: meta.skills }
      })
      .sort((a, b) => b.match - a.match)
      .slice(0, FOR_YOU_MAX)
  }, [traitProfile])

  const hasTraitData = derivedPaths.length > 0
  const careerPaths: CareerPath[] = hasTraitData ? derivedPaths : FALLBACK_PATHS

  const handleExplore = (title: string) =>
    navigate(`/career-learning-path?career=${encodeURIComponent(title)}`)

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <Sparkles className="w-4 h-4 text-amber-500" />
            <h3 className="text-base font-black text-slate-900">
              {hasTraitData ? "Your Top Career Matches" : "Explore Career Paths"}
            </h3>
          </div>
          <p className="text-xs text-slate-500">
            {hasTraitData
              ? `Top ${FOR_YOU_MAX} careers matched to your trait profile`
              : "Take the psychometric assessment to see personalised matches"}
          </p>
        </div>
        <span className="text-xs text-slate-400 font-semibold bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100">
          {careerPaths.length} paths
        </span>
      </div>

      {!expanded && (
        <AutoScrollRow speed={0.5}>
          {careerPaths.map((career, i) => (
            <CareerCard
              key={career.title}
              career={career}
              palette={PALETTES[i % PALETTES.length]}
              compact
              onExplore={() => handleExplore(career.title)}
            />
          ))}
        </AutoScrollRow>
      )}

      <button
        onClick={() => setExpanded((v) => !v)}
        className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 hover:border-slate-300 transition-all active:scale-[0.98]"
      >
        {expanded ? (
          <><ChevronUp className="w-4 h-4" /> Collapse</>
        ) : (
          <><ChevronDown className="w-4 h-4" /> Show all {careerPaths.length} matches</>
        )}
      </button>

      {expanded && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          {careerPaths.map((career, i) => (
            <CareerCard
              key={`exp-${career.title}`}
              career={career}
              palette={PALETTES[i % PALETTES.length]}
              onExplore={() => handleExplore(career.title)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
